import { resolvePillPreset } from '@/components/common/pill/preset'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'

import {
	COLLECTOR_STATUS_FILTERS,
	isCollectorStatusFilter,
} from '../status-filter'
import { COLLECTOR_STATUS_PRESETS } from '../status-preset'

import type { CollectorStatusFilter } from '../status-filter'

type CollectorStatusFilterToggleProps = {
	value: CollectorStatusFilter
	/**
	 * Per-filter row counts, or `null` while the list is still loading — the one
	 * data-bearing part of this control, which is why the loading state is a prop
	 * rather than a second component (P-05: the skeleton renders this same export,
	 * so the two cannot drift).
	 */
	counts: Record<CollectorStatusFilter, number> | null
	onChange: (value: CollectorStatusFilter) => void
}

/**
 * The status filter above the Collectors list: one option per Collector status
 * plus `All`, each carrying how many rows it would show.
 *
 * Labels come from `COLLECTOR_STATUS_PRESETS`, not from strings written here, so
 * the option and the row's pill can never read differently for the same status.
 *
 * Deselecting the active option is refused: a status filter with nothing selected
 * describes no set of rows. `All` is how you clear it.
 */
export const CollectorStatusFilterToggle = ({
	value,
	counts,
	onChange,
}: CollectorStatusFilterToggleProps) => {
	return (
		<ToggleGroup
			aria-label="Filter collectors by status"
			variant="outline"
			value={[value]}
			// Disabled rather than hidden while counts are pending: the control is
			// already correct (it reads the URL, not the data) and hiding it would
			// move every row down the moment the list resolves.
			disabled={counts === null}
			onValueChange={(next: string[]) => {
				// The primitive hands back bare strings, so the value is narrowed
				// against the vocabulary rather than asserted into it — this control
				// can then never put a status in the URL the list cannot read.
				const selected = next[0]
				if (isCollectorStatusFilter(selected) && selected !== value) {
					onChange(selected)
				}
			}}
		>
			{COLLECTOR_STATUS_FILTERS.map((filter) => (
				<ToggleGroupItem key={filter} value={filter} className="h-9 gap-2">
					{filterLabel(filter)}
					{counts === null ? (
						// `Badge`'s box (h-5, fully rounded) so the option doesn't resize
						// when the count lands. Overrides `Skeleton`'s `bg-muted`, which is
						// all but invisible against an unselected toggle item — the default
						// only reads on a card surface.
						<Skeleton className="bg-muted-foreground/20 h-5 w-6 rounded-4xl" />
					) : (
						<Badge variant="secondary">{counts[filter]}</Badge>
					)}
				</ToggleGroupItem>
			))}
		</ToggleGroup>
	)
}

/** `All`, or the status's one canonical label (ADR 0020 §5). */
function filterLabel(filter: CollectorStatusFilter): string {
	if (filter === 'all') return 'All'
	return resolvePillPreset(filter, COLLECTOR_STATUS_PRESETS).label
}
